import { PublicClientApplication, type AccountInfo, type Configuration, InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  markTokenExpired,
  markTokenValid,
} from './tokenStatus';

// Scopes for v1: full read+write to a user's accessible SharePoint + OneDrive.
// User.Read is the baseline needed to identify the linked MS account on the users row.
// Group.ReadWrite.All is required by the portal-wizard Function App's
// `getOrCreateGroupSite` (POST /groups creates a Group-connected Team site).
export const GRAPH_SCOPES = [
  'User.Read',
  'Files.ReadWrite.All',
  'Sites.ReadWrite.All',
  'Group.ReadWrite.All',
] as const;

/**
 * Stable sentinel used by `acquireGraphToken` when no MSAL account is
 * connected. Callers (e.g. ImportWizard) branch on `err.code === this`
 * to render an actionable "Connect Microsoft account" CTA instead of
 * dumping a developer-facing string into a red div.
 */
export const NO_MS_ACCOUNT_ERROR_CODE = 'no_ms_account_connected';

let app: PublicClientApplication | null = null;
let initPromise: Promise<void> | null = null;

function getConfig(): Configuration {
  const tenantId = import.meta.env.VITE_MSAL_TENANT_ID as string | undefined;
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID as string | undefined;
  const redirectUriEnv = import.meta.env.VITE_MSAL_REDIRECT_URI as string | undefined;

  if (!clientId) {
    throw new Error('VITE_MSAL_CLIENT_ID is required to connect Microsoft 365.');
  }

  // Default to /common for multi-tenant; allow override for single-tenant apps.
  const authority = `https://login.microsoftonline.com/${tenantId ?? 'common'}`;

  // Auto-derive the redirect URI from the current origin so the popup
  // polling stays same-origin across dev ports (5091 / 5092 / 3091).
  // The env var only wins if it explicitly matches the current origin —
  // otherwise we recompute. This dodges the trap where a stale .env.local
  // points 5091 but the SPA is served on 3091 and MSAL's parent-popup URL
  // polling fails the same-origin check.
  const computedRedirect = `${window.location.origin}/auth-redirect.html`;
  const redirectUri =
    redirectUriEnv && redirectUriEnv.startsWith(window.location.origin)
      ? redirectUriEnv
      : computedRedirect;

  return {
    auth: {
      clientId,
      authority,
      redirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      // Match the per-portal MSAL registry's F16.J fix: the default 60s
      // popup-bridge timeout cuts off first-time consent + tenant picker
      // mid-flight with `BrowserAuthError: timed_out`
      // (https://aka.ms/msal.js.errors#timed_out). 180s covers human-time
      // for the consent screen + 2FA without breaking the cached-cred
      // happy path (still resolves in < 5s when there's nothing to do).
      popupBridgeTimeout: 180_000,
      // Same hardening for the silent-SSO iframe bridge. MSAL hits this
      // when acquireTokenSilent has to refresh via /authorize iframe; if
      // third-party cookies are blocked the iframe never posts back and
      // the call hangs the 60s default before throwing.
      iframeBridgeTimeout: 30_000,
      // Verbose MSAL diagnostics in dev so timed-out failures surface
      // their actual root cause in the console.
      loggerOptions: {
        loggerCallback: (level: number, message: string) => {
          if (level <= 1) console.error('[MSAL]', message);
          else if (level === 2) console.warn('[MSAL]', message);
        },
        logLevel: 2,
        piiLoggingEnabled: false,
      },
    },
  };
}

/** Lazily initialize the singleton MSAL PublicClientApplication. */
export async function getMsalApp(): Promise<PublicClientApplication> {
  if (app) return app;
  if (!initPromise) {
    const instance = new PublicClientApplication(getConfig());
    initPromise = instance.initialize().then(async () => {
      // After initialize(), MSAL must consume the URL hash left behind by
      // loginRedirect or any degraded popup→redirect fallback. Without
      // this, returning from login.microsoftonline.com leaves #code=... in
      // the URL and the active account never gets set. Microsoft's docs:
      // "handleRedirectPromise should be invoked on every page load."
      try {
        const result = await instance.handleRedirectPromise();
        if (result !== null && result.account !== null) {
          instance.setActiveAccount(result.account);
        }
      } catch (err) {
        console.warn('[msalClient] handleRedirectPromise failed:', err);
      }
      // After the redirect-promise consumes the hash, restore the full URL
      // (path + query + hash) the caller had before signing in. This dodges
      // the `?test=1` and `/c/<slug>/...` getting stripped because the
      // Azure app reg only accepts a base host+path redirect URI.
      restoreReturnUrl();
      app = instance;
    });
  }
  await initPromise;
  if (!app) {
    throw new Error('MSAL initialization failed');
  }
  return app;
}

export function getActiveAccount(): AccountInfo | null {
  if (!app) return null;
  const active = app.getActiveAccount();
  if (active) return active;
  const all = app.getAllAccounts();
  return all[0] ?? null;
}

// Used by the portal-creation wizard to capture the operator's Azure AD
// tenant ID at provision time and lock it onto the new portal's
// tenantBranding row. With a multi-tenant MSAL authority (/common),
// this is the only way to know which tenant the operator authenticated
// against.
export function getMsalAccountTenantId(): string | null {
  return getActiveAccount()?.tenantId ?? null;
}

/** Pop a sign-in dialog so the user can link a Microsoft account to Repancy. */
export async function connectMicrosoftAccount(): Promise<AccountInfo> {
  const instance = await getMsalApp();
  const result = await instance.loginPopup({ scopes: [...GRAPH_SCOPES] });
  instance.setActiveAccount(result.account);
  return result.account;
}

/**
 * Full-page redirect sign-in. Use when loginPopup fails reliably (Chrome +
 * COOP strips `window.opener` after Microsoft's nav so MSAL's popup-bridge
 * can't post back — the same root cause F16.L addressed for portal sign-in).
 *
 * Callers MUST stash any unsaved state they want to survive the redirect
 * round-trip in sessionStorage before invoking. On return, MSAL's
 * handleRedirectPromise in `getMsalApp` consumes the hash and sets the
 * active account; the caller's resume code (keyed off the sessionStorage
 * stash) takes over.
 *
 * This function navigates away — the returned promise never resolves in the
 * normal flow.
 */
export async function connectMicrosoftAccountRedirect(opts?: {
  redirectStartPage?: string;
}): Promise<void> {
  const instance = await getMsalApp();
  // Clear stale interaction.status locks from any prior popup attempt;
  // F16.L pattern.
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('msal.') && key.endsWith('interaction.status')) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // sessionStorage unavailable — non-fatal.
  }
  // Azure validates redirect URI host+path exactly (case-sensitive). To
  // preserve `?test=1` / /c/<slug>/<page> without registering N URIs in
  // Azure, we stash the full current URL in sessionStorage BEFORE
  // redirect and restore it AFTER handleRedirectPromise consumes the
  // hash (see restoreReturnUrl()).
  //
  // The PCA's default `redirectUri` is `auth-redirect.html` — that
  // page is popup-only (window.opener.postMessage + self-close), so
  // using it as the redirect target for loginRedirect lands the user
  // on a static page that just tries to close itself. For redirect
  // flow we MUST land back inside the SPA so handleRedirectPromise
  // can consume the hash. We use the origin root (`/`) — which is the
  // canonical "SPA redirect URI" Microsoft expects to be registered
  // in the app reg under "Single-page application" platform.
  //
  // PREREQUISITE (Azure app reg): under "Authentication" → SPA
  // platform, the following redirect URIs MUST be listed:
  //   - http://localhost:5091/                      (dev SPA root)
  //   - http://localhost:5091/auth-redirect.html    (dev popup target)
  //   - https://<netlify-prod-host>/                (prod SPA root)
  //   - https://<netlify-prod-host>/auth-redirect.html (prod popup)
  // Add via Azure portal or `az ad app update --web-redirect-uris`.
  // History: earlier this passed `${origin}${pathname}` which made
  // every portal-deep-link path (/c/<slug>/desktop) hit Azure as an
  // unregistered URI and fail with AADSTS50011.
  const rootRedirectUri = `${window.location.origin}/`;
  try {
    sessionStorage.setItem(
      MSAL_RETURN_URL_KEY,
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  } catch {
    /* sessionStorage unavailable — non-fatal */
  }
  await instance.loginRedirect({
    scopes: [...GRAPH_SCOPES],
    redirectUri: rootRedirectUri,
    redirectStartPage:
      opts?.redirectStartPage ??
      `${window.location.pathname}${window.location.search}`,
  });
}

/** sessionStorage key for the post-redirect URL restoration. */
export const MSAL_RETURN_URL_KEY = 'repancy:msal-return-url';

/**
 * Restore the full URL (path + query + hash) that was stashed before
 * `connectMicrosoftAccountRedirect`. Call once after MSAL init completes
 * — `getMsalApp` does this at the end of its handleRedirectPromise block.
 * Idempotent; clears the stash on first call.
 */
function restoreReturnUrl(): void {
  try {
    const stored = sessionStorage.getItem(MSAL_RETURN_URL_KEY);
    if (stored === null) return;
    sessionStorage.removeItem(MSAL_RETURN_URL_KEY);
    // Don't navigate if we're already there. Compare path + search + hash.
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (current === stored) return;
    // Use replaceState rather than location.assign to avoid a full reload
    // and to keep React state alive.
    window.history.replaceState({}, '', stored);
  } catch {
    /* sessionStorage unavailable — non-fatal */
  }
}

/** Clear MSAL's session-storage cache and forget the active account. */
export async function disconnectMicrosoftAccount(): Promise<void> {
  const instance = await getMsalApp();
  const account = getActiveAccount();
  if (!account) return;
  await instance.clearCache({ account });
  instance.setActiveAccount(null);
}

/**
 * F19.3 — full MSAL session reset. Called by `useMsalSessionBinding`
 * (and the non-hook variant `subscribeMsalToClerkSession`) from
 * `lib/graph/msalSession.ts` when the Clerk identity transitions
 * (sign-out, or sign-in as a different user). Earlier F19 design docs
 * referred to this entry point as `bindMsalToClerk`; that name never
 * landed in code — the two exports above are the real API.
 * Differs from `disconnectMicrosoftAccount` (which is for "user clicked
 * Disconnect" UX): clears EVERY account, drops the entire MSAL cache,
 * AND wipes Repancy-scoped localStorage keys that hold per-session UI
 * state (selected portal, demo session id, etc.) so the next Clerk
 * user doesn't inherit the prior user's UI fingerprint.
 *
 * Idempotent — safe to call when no MSAL session exists.
 *
 * `demoSessionId` rotation (F19.6 territory) hooks into this same
 * chokepoint via the optional `rotateDemoSessionId` callback so F19.3
 * + F19.6 share the single sign-out cleanup point.
 *
 * Returns the count of items dropped so smokes / tests can assert.
 */
export interface ClearMsalSessionResult {
  msalAccountsRemoved: number;
  localStorageKeysDropped: number;
  cacheCleared: boolean;
}

export async function clearMsalSession(
  opts: {
    /** Drop localStorage keys matching this prefix. Default 'repancy:'. */
    keyPrefix?: string;
    /** Keys to keep even if they match `keyPrefix`. */
    preserveKeys?: ReadonlyArray<string>;
  } = {},
): Promise<ClearMsalSessionResult> {
  const keyPrefix = opts.keyPrefix ?? 'repancy:';
  const preserveKeys = new Set(opts.preserveKeys ?? []);
  let msalAccountsRemoved = 0;
  let cacheCleared = false;
  // Touch MSAL only if it's been initialized; never lazy-init on sign-out
  // since the user might sign out before MSAL ever loaded.
  if (app !== null) {
    try {
      const accounts = app.getAllAccounts();
      for (const account of accounts) {
        try {
          await app.clearCache({ account });
          msalAccountsRemoved += 1;
        } catch (err) {
          console.warn('[clearMsalSession] clearCache failed for account', err);
        }
      }
      try {
        // Final sweep — clearCache({account}) only removes per-account
        // tokens; this also drops cross-account state (in-progress
        // interaction.status entries, etc.).
        await app.clearCache();
        cacheCleared = true;
      } catch (err) {
        console.warn('[clearMsalSession] global clearCache failed', err);
      }
      app.setActiveAccount(null);
    } catch (err) {
      console.warn('[clearMsalSession] MSAL teardown threw', err);
    }
  }
  // Drop Repancy-scoped localStorage keys. We don't touch sessionStorage
  // for MSAL keys directly — `clearCache()` already handles those when
  // `cacheLocation: 'sessionStorage'`.
  let localStorageKeysDropped = 0;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key === null) continue;
      if (!key.startsWith(keyPrefix)) continue;
      if (preserveKeys.has(key)) continue;
      keysToRemove.push(key);
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
      localStorageKeysDropped += 1;
    }
  } catch (err) {
    console.warn('[clearMsalSession] localStorage scan failed', err);
  }
  return { msalAccountsRemoved, localStorageKeysDropped, cacheCleared };
}

/**
 * Acquire a Graph access token WITHOUT ever opening a popup.
 *
 * Returns the token if MSAL has a valid session, `null` if no account is
 * connected or the cached token has expired and interactive consent
 * would be required. Never throws on InteractionRequiredAuthError —
 * callers handle the `null` case by falling back to server-side
 * app-only mint (kickPortalProvisioner's K.2 branch).
 *
 * USE THIS for any code path that runs WITHOUT an explicit user gesture
 * (autorun, retry-after-failure, programmatic kick after slug confirm).
 * Popups in those contexts get blocked or hang indefinitely — see the
 * 2026-05-22 demo-firm-one zombie-row incident.
 *
 * Use `acquireGraphToken` (interactive) only inside an explicit click
 * handler where the user expects a popup (the "Sign in with Microsoft"
 * button + the BYO-tenant browsing flow).
 */
export async function acquireGraphTokenSilent(): Promise<string | null> {
  const instance = await getMsalApp();
  const account = getActiveAccount();
  if (!account) return null;
  try {
    const result = await instance.acquireTokenSilent({
      scopes: [...GRAPH_SCOPES],
      account,
    });
    if (result.expiresOn !== null) {
      markTokenValid({
        expiresOn: result.expiresOn,
        accountEmail: account.username,
      });
    }
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      markTokenExpired();
      return null;
    }
    // Network / config errors → null, let caller fall back to app-only.
    return null;
  }
}

/**
 * Acquire a Graph access token. Tries silent first (uses session cache);
 * falls back to interactive popup if MSAL signals consent or interaction is required.
 */
export async function acquireGraphToken(): Promise<string> {
  const instance = await getMsalApp();
  const account = getActiveAccount();
  if (!account) {
    // Stable sentinel so callers (e.g. ImportWizard) can branch and
    // render a "Connect Microsoft account" CTA instead of dumping the
    // raw message into a red div. Keep the human sentence too so any
    // caller that just toasts the message still says something sane.
    const err = new Error('Microsoft account not connected.') as Error & {
      code?: string;
    };
    err.code = NO_MS_ACCOUNT_ERROR_CODE;
    throw err;
  }

  try {
    const result = await instance.acquireTokenSilent({
      scopes: [...GRAPH_SCOPES],
      account,
    });
    // B.7.c.2 — surface the live expiry into the token-status store so
    // the MicrosoftAccountPanel can render a valid/expiring-soon badge
    // without polling.
    if (result.expiresOn !== null) {
      markTokenValid({
        expiresOn: result.expiresOn,
        accountEmail: account.username,
      });
    }
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // The silent acquire failed because MSAL needs a fresh interactive
      // login. Flip the status so the panel's Reconnect button lights up.
      markTokenExpired();
      const result = await instance.acquireTokenPopup({
        scopes: [...GRAPH_SCOPES],
        account,
      });
      if (result.expiresOn !== null) {
        markTokenValid({
          expiresOn: result.expiresOn,
          accountEmail: account.username,
        });
      }
      return result.accessToken;
    }
    throw err;
  }
}
